@if (visible && (!headerHidden || store.shownFromHeader)) {
  <div
    #dock
    class="assistant-dock"
    [class.assistant-dock--left]="anchorX === 'left'"
    [class.assistant-dock--right]="anchorX === 'right'"
    [class.assistant-dock--top]="anchorY === 'top'"
    [class.assistant-dock--bottom]="anchorY === 'bottom'"
    [style.left.px]="anchorX === 'left' ? offsetX : null"
    [style.right.px]="anchorX === 'right' ? offsetX : null"
    [style.top.px]="anchorY === 'top' ? offsetY : null"
    [style.bottom.px]="anchorY === 'bottom' ? offsetY : null"
  >
    @if (store.mode === 'closed') {
      <button
        type="button"
        class="assistant-launcher"
        [class.assistant-launcher--dragging]="dragging"
        [matTooltip]="'SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_OPEN' | translate"
        [matTooltipDisabled]="dragging"
        [matTooltipPosition]="anchorX === 'left' ? 'right' : 'left'"
        [attr.aria-label]="'SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_OPEN' | translate"
        (pointerdown)="startDrag($event)"
        (click)="openCompact()"
      >
        <app-walley [size]="96" [decorative]="true" [ambient]="true" />
      </button>
    } @else {
      <div class="assistant-shell">
        <button
          type="button"
          class="assistant-resize"
          [attr.aria-label]="'SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_RESIZE' | translate"
          (pointerdown)="startResize($event)"
        >
          <span class="assistant-resize__tip">
            {{ 'SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_RESIZE' | translate }}
          </span>
        </button>
        <section
          class="assistant-window card border-0 rounded-4 shadow"
          [class.assistant-window--expanded]="store.mode === 'expanded' && store.width == null"
          [style.width.px]="store.width"
          [style.height.px]="store.height"
          [attr.aria-label]="'SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_TITLE' | translate"
        >
        <header class="assistant-window__header">
          <div class="d-flex justify-content-between align-items-center gap-2">
            <strong>{{ 'SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_TITLE' | translate }}</strong>
            <div class="d-flex align-items-center gap-2">
              <span class="badge rounded-pill text-bg-light border fw-normal">
                {{ 'SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_READONLY' | translate }}
              </span>
              <button
                type="button"
                class="btn btn-sm btn-light rounded-circle assistant-window__icon"
                [matTooltip]="'SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_CLOSE' | translate"
                [attr.aria-label]="'SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_CLOSE' | translate"
                (click)="close()"
              >
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
          </div>
          <p class="small text-muted mb-0 mt-1">
            {{ subtitle() }}
          </p>
        </header>

        @if (store.mode === 'expanded' || store.error) {
          <div class="assistant-window__body">
            @if (store.mode === 'expanded') {
              <div class="assistant-window__messages">
                @if (store.messages.length === 0) {
                  <div class="text-muted small">
                    {{ 'SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_EMPTY' | translate }}
                  </div>
                }
                @for (msg of store.messages; track $index) {
                  <div
                    class="assistant-bubble"
                    [class.assistant-bubble--user]="msg.role === 'user'"
                    [class.assistant-bubble--assistant]="msg.role === 'assistant'"
                  >
                    <div class="assistant-reply">
                      @for (paragraph of replyParagraphs(msg.text); track $index) {
                        <p>
                          @for (line of paragraph; track $index) {
                            @if (!$first) {
                              <br />
                            }
                            @for (part of line; track $index) {
                              @if (part.bold) {
                                <strong>{{ part.text }}</strong>
                              } @else {
                                {{ part.text }}
                              }
                            }
                          }
                        </p>
                      }
                    </div>
                    @if (msg.table?.rows?.length) {
                      <div class="table-responsive assistant-table-wrap mt-2">
                        <table class="table table-sm align-middle mb-0 assistant-table">
                        <thead>
                          <tr>
                            @for (col of msg.table!.columns; track col) {
                              <th>{{ columnLabel(col) | translate }}</th>
                            }
                          </tr>
                        </thead>
                        <tbody>
                          @for (row of msg.table!.rows; track $index) {
                            <tr>
                              @for (col of msg.table!.columns; track col) {
                                <td>
                                  @for (part of cellParts(col, row); track $index) {
                                    @if (!$first) {
                                      <span>, </span>
                                    }
                                    @if (part.route) {
                                      <a [routerLink]="part.route" class="assistant-link" [attr.aria-label]="part.label">{{ part.text }}</a>
                                    } @else {
                                      {{ part.text }}
                                    }
                                  }
                                </td>
                              }
                            </tr>
                          }
                        </tbody>
                      </table>
                    </div>
                  }
                </div>
              }
                @if (store.loading) {
                  <div class="assistant-bubble assistant-bubble--assistant small text-muted">
                    {{ 'SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_THINKING' | translate }}
                  </div>
                }
              </div>

              @if (store.lastSources.length > 0) {
                <div class="small text-muted mt-2">
                  {{ 'SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_SOURCES' | translate }}:
                  {{ store.lastSources.length }}
                </div>
              }
            }

            @if (store.error) {
              <div class="alert alert-warning py-2 mb-0">{{ store.error }}</div>
            }
          </div>
        }

        <footer class="assistant-window__footer">
          <div class="d-flex flex-wrap gap-2 mb-2">
            @for (suggestion of suggestions; track suggestion) {
              <button
                type="button"
                class="btn btn-sm btn-light border rounded-pill"
                (click)="sendSuggestion(suggestion)"
              >
                {{ suggestion | translate }}
              </button>
            }
          </div>
          <div class="assistant-composer">
            <input
              type="text"
              class="form-control border-0 shadow-none bg-transparent"
              [formControl]="messageControl"
              [placeholder]="'SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_PLACEHOLDER' | translate"
              (keydown.enter)="send()"
            />
            <button
              type="button"
              class="btn btn-primary rounded-circle assistant-composer__send"
              [disabled]="store.loading || !messageControl.value.trim()"
              [attr.aria-label]="'SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_PLACEHOLDER' | translate"
              (click)="send()"
            >
              <i class="bi bi-send"></i>
            </button>
          </div>
        </footer>
        </section>
      </div>
    }
  </div>
}
